/**
 * Collapses identical reads that happen at (nearly) the same moment into one network request.
 *
 * Why this exists: the home page renders the same discovery cards twice — once in the inline mobile
 * carousel, once in the desktop rail — and the rail is hidden with CSS, which hides a *render*, not a
 * *mount*. Both copies mount, both effects run, and every card fetched its data twice. See
 * `designs/01-now/2026-07-24-home-discovery-duplicate-mount-design.md`.
 *
 * Three behaviours are deliberate:
 *
 * 1. The reuse window extends a little past resolution (`ttlMs`). The measured duplicates were ~400 ms
 *    apart, so the first request has usually already settled by the time the second subtree commits.
 *    In-flight-only deduplication would have missed exactly the case this was built for.
 * 2. A rejection is never cached. Both callers receive the same rejection (nobody is left waiting on a
 *    promise that was quietly dropped) and the entry is removed immediately, so the next mount retries.
 * 3. Resolved values are frozen. One object is now shared by every caller, so accidental mutation by
 *    one consumer must not be able to reach the others.
 *
 * This is the `inFlightSearches` idiom from `TagSuggestionService` / `UserSuggestionService`, extracted
 * so it is not hand-rolled a third time. Those services keep their own copies: they layer
 * query-specific negative caching and failure back-off on top, which does not generalize.
 */

export const DEFAULT_COALESCE_TTL_MS = 2_000;

const MAX_TRACKED_KEYS = 200;

type CoalescedEntry<T> = {
    promise: Promise<T>;
    /** `null` while the request is in flight; a timestamp once it has resolved. */
    reusableUntil: number | null;
};

function freezeValue<T>(value: T): T {
    if (value !== null && typeof value === "object") {
        return Object.freeze(value);
    }

    return value;
}

export class RequestCoalescer<T> {
    private readonly entries = new Map<string, CoalescedEntry<T>>();

    constructor(private readonly ttlMs: number = DEFAULT_COALESCE_TTL_MS) {
    }

    private isReusable(entry: CoalescedEntry<T>): boolean {
        return entry.reusableUntil === null || entry.reusableUntil > Date.now();
    }

    private prune() {
        for (const [key, entry] of this.entries.entries()) {
            if (!this.isReusable(entry)) {
                this.entries.delete(key);
            }
        }

        while (this.entries.size > MAX_TRACKED_KEYS) {
            const oldestKey = this.entries.keys().next().value;
            if (typeof oldestKey !== "string") {
                return;
            }
            this.entries.delete(oldestKey);
        }
    }

    run(key: string, request: () => Promise<T>): Promise<T> {
        this.prune();

        const existing = this.entries.get(key);
        if (existing && this.isReusable(existing)) {
            return existing.promise;
        }

        const entry: CoalescedEntry<T> = {
            // Assigned immediately below; the indirection is what lets the settle handlers refer to
            // their own entry and avoid clobbering a newer one for the same key.
            promise: undefined as unknown as Promise<T>,
            reusableUntil: null,
        };

        entry.promise = request().then(
            (value) => {
                if (this.entries.get(key) === entry) {
                    entry.reusableUntil = Date.now() + this.ttlMs;
                }
                return freezeValue(value);
            },
            (error) => {
                if (this.entries.get(key) === entry) {
                    this.entries.delete(key);
                }
                throw error;
            },
        );

        this.entries.set(key, entry);
        return entry.promise;
    }

    /** Drop one key, e.g. after a mutation that changes what the read would return. */
    invalidate(key: string) {
        this.entries.delete(key);
    }

    clear() {
        this.entries.clear();
    }
}
